import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ImageIcon, Loader2, Trash2, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { organizationService } from '../../services/api';
import { useTenant } from '../../contexts/TenantContext';
import { userFacingError } from '../../i18n/userFacingError';
import { tenantBrand } from '../../utils/tenantBranding';

const SLOTS = [
  {
    kind: 'logo',
    titleKey: 'brandingLogo',
    hintKey: 'brandingLogoHint',
    accept: 'image/png,image/jpeg,image/webp,image/svg+xml',
    preview: 'logo',
  },
  {
    kind: 'banner',
    titleKey: 'brandingBanner',
    hintKey: 'brandingBannerHint',
    accept: 'image/png,image/jpeg,image/webp',
    preview: 'banner',
  },
  {
    kind: 'coat_of_arms',
    titleKey: 'brandingCoat',
    hintKey: 'brandingCoatHint',
    accept: 'image/png,image/jpeg,image/webp,image/svg+xml',
    preview: 'coat',
  },
];

function previewUrl(brand, slot) {
  if (slot.preview === 'banner') return brand.bannerUrl;
  if (slot.preview === 'coat') return brand.coatOfArmsUrl;
  return brand.logoUrl;
}

export default function SettingsBrandingTab({ headerBar }) {
  const { t } = useTranslation('settings');
  const { tenant, patchTenant } = useTenant();
  const [org, setOrg] = useState(tenant);
  const [loading, setLoading] = useState(true);
  const [busyKind, setBusyKind] = useState('');

  useEffect(() => {
    let active = true;
    organizationService
      .getPublicChannels()
      .then(({ data }) => {
        if (!active) return;
        setOrg(data);
      })
      .catch((error) => {
        if (!active) return;
        setOrg(tenant);
        toast.error(userFacingError(error, 'settings:brandingLoadError'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tenant]);

  const applyOrg = (data) => {
    setOrg(data);
    patchTenant?.({
      logo_url: data?.logo_url || '',
      banner_url: data?.banner_url || '',
      coat_of_arms_url: data?.coat_of_arms_url || '',
    });
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setBusyKind(kind);
    try {
      const { data } = await organizationService.uploadBranding(kind, file);
      applyOrg(data);
      toast.success(t('brandingUploaded'));
    } catch (error) {
      toast.error(userFacingError(error, 'settings:brandingUploadError'));
    } finally {
      setBusyKind('');
    }
  };

  const remove = async (kind) => {
    setBusyKind(kind);
    try {
      const { data } = await organizationService.deleteBranding(kind);
      applyOrg(data);
      toast.success(t('brandingRemoved'));
    } catch (error) {
      toast.error(userFacingError(error, 'settings:brandingRemoveError'));
    } finally {
      setBusyKind('');
    }
  };

  if (loading) {
    return (
      <div className="data-list-card flex items-center justify-center gap-2 p-10 text-gray-600">
        <Loader2 className="h-5 w-5 animate-spin" />
        {t('brandingLoading')}
      </div>
    );
  }

  const brand = tenantBrand(org);

  return (
    <div className="data-list-card">
      {headerBar(ImageIcon, t('brandingTitle'), t('brandingDescription'))}
      <div className="space-y-5 p-5 sm:p-6">
        <p className="text-sm text-gray-600">{t('brandingNote')}</p>
        <div className="grid gap-4 lg:grid-cols-3">
          {SLOTS.map((slot) => {
            const src = previewUrl(brand, slot);
            const busy = busyKind === slot.kind;
            return (
              <div key={slot.kind} className="rounded-xl border border-gray-100 bg-slate-50/70 p-4">
                <p className="text-sm font-semibold text-gray-900">{t(slot.titleKey)}</p>
                <p className="mt-0.5 text-xs text-gray-500">{t(slot.hintKey)}</p>
                <div
                  className={`mt-3 flex items-center justify-center overflow-hidden rounded-lg border border-dashed ${
                    slot.preview === 'banner'
                      ? 'h-24 border-gray-200 bg-white'
                      : slot.preview === 'coat'
                        ? 'h-28 border-gray-200 bg-[linear-gradient(45deg,#e2e8f0_25%,#fff_25%,#fff_75%,#e2e8f0_75%),linear-gradient(45deg,#e2e8f0_25%,#fff_25%,#fff_75%,#e2e8f0_75%)] bg-[length:14px_14px] bg-[position:0_0,7px_7px]'
                        : 'h-28 border-gray-200 bg-white'
                  }`}
                >
                  {src ? (
                    <img src={src} alt="" className="max-h-full max-w-full object-contain" />
                  ) : (
                    <span
                      className="px-3 text-center text-xs text-gray-400"
                    >
                      {t('brandingEmpty')}
                    </span>
                  )}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <label className="btn btn-outline inline-flex cursor-pointer items-center gap-1.5 text-sm">
                    <Upload className="h-4 w-4" aria-hidden />
                    {busy ? t('brandingUploading') : t('brandingChoose')}
                    <input
                      type="file"
                      accept={slot.accept}
                      className="sr-only"
                      disabled={Boolean(busyKind)}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        event.target.value = '';
                        upload(slot.kind, file);
                      }}
                    />
                  </label>
                  {src ? (
                    <button
                      type="button"
                      className="btn btn-outline inline-flex items-center gap-1.5 text-sm text-danger-700"
                      disabled={Boolean(busyKind)}
                      onClick={() => remove(slot.kind)}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                      {t('brandingRemove')}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
